import ERRORS from "common/errors";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { compose } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import AppInput from "components/app-input";
import { useState, useMemo, useCallback, useRef, useImperativeHandle, memo, forwardRef } from "react";
import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";
import AppCalendarInput from "components/app-calendar-input";
import { CALENDAR_FORMAT } from "common/calendar";
import AppButtonInput from "components/app-button-input";
import COMMON from "common";
import AppSelectInput from "components/app-select-input";
import AppMobileInput from "components/app-mobile-input";
import { Modal } from "@mui/material";
import getStateListing from "services/get-state-listing";
import getPostcodeListing from "services/get-postcode-listing";
import getCitiesListing from "services/get-cities-listing";
import AppCheckbox from "../../app-checkbox";
import getRelationshipListing from "services/get-relationship-listing";
import AppButton from "../../app-button";
import AppWelcomeOnboardModal from "./app-welcome-onboard-modal";
import classNames from "common/class-names";
import getBankListing from "services/get-bank-listing";
import withProfile from "contexts/with-profile";
const calendarDisplayFormat = CALENDAR_FORMAT.DATE_FORMAT + " " + CALENDAR_FORMAT.MONTH_FORMAT + " " + CALENDAR_FORMAT.YEAR_FORMAT;

const AppEmployeeFirstTimeInfoModal = forwardRef(({ onHandleGetProfile, employeeInfo }, ref) => {
    const bankRef = useRef();

    const dietarySelections = [
        { label: "Halal", value: "HALAL" },
        { label: "Vegan", value: "VEGAN" },
        { label: "Vegetarian", value: "VEGETARIAN" },
        { label: "Other", value: "OTHER" }
    ];

    const dietaryValues = dietarySelections.map(option => option.value);

    const initialValues = useMemo(() => ({
        gender: employeeInfo?.personalInfo?.gender || "",
        nationality: employeeInfo?.personalInfo?.nationality || "",
        maritalStatus: employeeInfo?.personalInfo?.maritalStatus || "",
        nickname: "",
        correspondenceAddress1: "",
        correspondenceAddress2: "",
        state: "",
        region: "",
        postcode: "",
        permanentAddress: false,
        residentialAddress1: "",
        residentialAddress2: "",
        residentialState: "",
        residentialPostcode: "",
        emergencyContactFullName: "",
        emergencyContactRelationship: "",
        emergencyContactMobileNo: "",
        emergencyContactMobileNoPrefix: "",
        emergencyContactFullName2: "",
		emergencyContactRelationship2: "",
		emergencyContactMobileNo2: "",
		emergencyContactPrefixNo2: "",
        dietaryPreferences: "",
        dietaryOptions: [],
        dietaryDescription: "",
        foodAllergy: "",
        foodAllergyDetails: "",
        medicalCondition: "",
        medicalConditionDetails: "",
        longTermMedication: "",
        longTermMedicationDetails: "",
        infoConfirmed: "",
        acknowledgePolicy: "",
        bank: employeeInfo?.bank?.bankName || "",
        bankAccountNo: employeeInfo?.bank?.bankAccountNo || "",
        swiftCode: employeeInfo?.bank?.swiftCode || "",
        epfNo: employeeInfo?.contribution?.epfNo || "",
        incomeTaxNo: employeeInfo?.contribution?.incomeTaxNo || "",
        socsoNo: employeeInfo?.contribution?.socsoNo || ""
    }), [employeeInfo]);
    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: yup.object({
            gender: yup.string().required(ERRORS.REQUIRED),
            nationality: yup.string().required(ERRORS.REQUIRED),
            maritalStatus: yup.string().required(ERRORS.REQUIRED),
			nickname: yup.string().matches(COMMON.REGEX.ALPHABETIC, ERRORS.ALPHABET_ONLY).required(ERRORS.REQUIRED),
            correspondenceAddress1: yup.string().required(ERRORS.REQUIRED),
            state: yup.string().required(ERRORS.REQUIRED),
            region: yup.string().required(ERRORS.REQUIRED),
            postcode: yup.string().required(ERRORS.REQUIRED),
            residentialAddress1: yup.string().when(["permanentAddress"], {
                is: (permanentAddress) => !permanentAddress,
                then: () => yup.string().required(ERRORS.REQUIRED),
            }),
            residentialState: yup.string().when(["permanentAddress"], {
                is: (permanentAddress) => !permanentAddress,
                then: () => yup.string().required(ERRORS.REQUIRED),
            }),
            residentialRegion: yup.string().when(["permanentAddress"], {
                is: (permanentAddress) => !permanentAddress,
                then: () => yup.string().required(ERRORS.REQUIRED),
            }),
            residentialPostcode: yup.string().when(["permanentAddress"], {
                is: (permanentAddress) => !permanentAddress,
                then: () => yup.string().required(ERRORS.REQUIRED),
            }),
            emergencyContactFullName: yup.string().matches(COMMON.REGEX.ALPHABETIC, ERRORS.ALPHABET_ONLY).required(ERRORS.REQUIRED),
            emergencyContactRelationship: yup.string().required(ERRORS.REQUIRED),
            emergencyContactMobileNoPrefix: yup.string().required(ERRORS.REQUIRED),
            emergencyContactMobileNo: yup.string().required(ERRORS.REQUIRED).min(8, ERRORS.MOBILE_NO_MIN).max(12, ERRORS.MOBILE_NO_MAX),
            emergencyContactFullName2: yup.string().matches(COMMON.REGEX.ALPHABETIC, ERRORS.ALPHABET_ONLY),
            emergencyContactsRelationship: yup.string(),
            emergencyContactMobileNo2: yup.string().min(8, ERRORS.MOBILE_NO_MIN).max(12, ERRORS.MOBILE_NO_MAX),
            dietaryPreferences: yup.boolean().required(ERRORS.REQUIRED),
            dietaryOptions: yup.array().of(yup.string().oneOf(dietaryValues)).when("dietaryPreferences", {
                is: true,
                then: (schema) => schema.min(1, "Please select at least one option.").required(ERRORS.REQUIRED),
                otherwise: (schema) => schema.notRequired()
            }),
            dietaryDescription: yup
                .string()
                .when("dietaryOptions", {
                    is: (options) => Array.isArray(options) && options.includes("OTHER"),
                    then: (schema) => schema.required(ERRORS.REQUIRED),
                    otherwise: (schema) => schema.notRequired()
                }),
            foodAllergy: yup.boolean().required(ERRORS.REQUIRED),
            foodAllergyDetails: yup.string().when("foodAllergy", {
                is: true,
                then: (schema) => schema.required(ERRORS.REQUIRED),
                otherwise: (schema) => schema.notRequired()
            }),
            medicalCondition: yup.boolean().required(ERRORS.REQUIRED),
            medicalConditionDetails: yup.string().when("medicalCondition", {
                is: true,
                then: (schema) => schema.required(ERRORS.REQUIRED),
                otherwise: (schema) => schema.notRequired()
            }),
            longTermMedication: yup.boolean().required(ERRORS.REQUIRED),
            longTermMedicationDetails: yup.string().when("longTermMedication", {
                is: true,
                then: (schema) => schema.required(ERRORS.REQUIRED),
                otherwise: (schema) => schema.notRequired()
            }),
            infoConfirmed: yup.boolean().oneOf([true], ERRORS.INFO_CONFIRMED).required(ERRORS.INFO_CONFIRMED),
			acknowledgePolicy: yup.boolean().oneOf([true], ERRORS.ACKNOWLEDGE_POLICY).required(ERRORS.ACKNOWLEDGE_POLICY),
            bank: yup.string().required(ERRORS.REQUIRED),
            bankAccountNo: yup.number().required(ERRORS.REQUIRED),
        }),
        onSubmit: (values) => {
            onHandleConfirm(values);
        }
    })

    const welcomeRef = useRef();
    const [visible, setVisible] = useState(false);
    const company = useSelector((state) => state.profile.company);
    const getCitiesLoadOptions = useCallback(() => getCitiesListing({ state: formik.values.state }), [formik.values.state]);
    const getPostcodeLoadOptions = useCallback(() => getPostcodeListing(`${formik.values.state}/${formik.values.region}`), [formik.values.state, formik.values.region]);
    const getResidentialCitiesLoadOptions = useCallback(() => getCitiesListing({ state: formik.values.residentialState }), [formik.values.residentialState]);
    const getResidentialPostcodeLoadOptions = useCallback(() => getPostcodeListing(`${formik.values.residentialState}/${formik.values.residentialRegion}`), [formik.values.residentialState, formik.values.residentialRegion]);

    const onHandleConfirm = async (values) => {
        let response = null;
        try {
            const dietaryLists = formik.values.dietaryOptions.map(option => ({
                dietaryOption: option,
                description: formik.values.dietaryDescription
            }));

            let payload = {
                gender: values.gender,
                nationality: values.nationality,
                maritalStatus: values.maritalStatus,
                businessRegistrationNo: company,
                nickname: values.nickname,
                address: {
                    correspondenceAddress1: values.correspondenceAddress1,
                    correspondenceAddress2: values.correspondenceAddress2,
                    correspondencePostcode: values.postcode,
                    correspondenceState: values.state,
                    correspondenceRegion: values.region,
                    sameAsCorrespondenceAddress: values.permanentAddress ? "Y" : "N",
                },
                emergencyContact: {
                    fullName: values.emergencyContactFullName,
                    relationship: values.emergencyContactRelationship,
                    mobileNoPrefix: values.emergencyContactMobileNoPrefix,
                    mobileNo: values.emergencyContactMobileNo,
                    fullName2: values.emergencyContactFullName2 || null,
					relationship2: values.emergencyContactRelationship2 || null,
					mobileNoPrefix2: values.emergencyContactPrefixNo2 || null,
					mobileNo2: values.emergencyContactMobileNo2 || null,
                },
                healthDeclaration: {
                    dietaryPreferences: values.dietaryPreferences,
                    dietaryOptions: dietaryLists,
                    foodAllergy: values.foodAllergy,
                    foodAllergyDetails: values.foodAllergyDetails,
                    medicalCondition: values.medicalCondition,
                    medicalConditionDetails: values.medicalConditionDetails,
                    longTermMedication: values.longTermMedication,
                    longTermMedicationDetails: values.longTermMedicationDetails,
                    infoConfirmed: values.infoConfirmed,
                    acknowledgePolicy: values.acknowledgePolicy,
                },
                bank: {
                    bankName: values.bank,
                    bankNameDesc: bankRef.current?.getOptionsData()?.find((o) => o.value === values.bank)?.label || null,
                    bankAccount: values.bankAccountNo,
                    swiftCode: values.swiftCode,
                },
                contribution: {
                    epfNo: values.epfNo,
                    socsoNo: values.socsoNo,
                    incomeTaxNo: values.incomeTaxNo,
                },
            }

            if (!values.permanentAddress) {
                payload = {
                    ...payload,
                    address: {
                        ...payload.address,
                        residentialAddress1: values.residentialAddress1,
                        residentialAddress2: values.residentialAddress2,
                        residentialPostcode: values.residentialPostcode,
                        residentialRegion: values.residentialRegion,
                        residentialState: values.residentialState,
                    },
                };
            }

            await api.post.employee.updateFirstTimeEmployeeInfo(payload);

            await onHandleGetProfile();

            response = true;

            if (response) {
                setVisible(false);
                welcomeRef.current?.onHandleShow();
            }

        } catch (error) {
            serveRequestErrors(error);
        } finally {
            formik.setSubmitting(false);
        }

    }

    const onHandleSelectPermanentAddress = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        formik.setFieldValue(name, !value);
    };

    const onHandleCheckbox = useCallback ((event) => {
		const name = event.target.name;
		const value = event.target.value;
		formik.setFieldValue(name, !value);
	}, [formik]);

    const onHandleSelectField = useCallback(async (event) => {
        const name = event.target.name;
        const value = event.target.value;

        switch (name) {
            case "state":
                formik.setFieldValue("region", "");
                formik.setFieldValue("postcode", "");
                break;
            case "region":
                formik.setFieldValue("postcode", "");
                break;
            case "postcode":
                break;
            case "residentialState":
                formik.setFieldValue("residentialRegion", "");
                formik.setFieldValue("residentialPostcode", "");
                break;
            case "residentialRegion":
                formik.setFieldValue("residentialPostcode", "");
                break;
            case "residentialPostcode":
                break;
            default:
                break;
        }

        formik.setFieldValue(name, value);
    }, [formik]);


    useImperativeHandle(ref, () => ({
        onHandleShow: () => {
            setVisible(true);
        }
    }))

    const rowWidthDietaryClassName = classNames({
        "half-width": formik.values.dietaryPreferences === true && formik.values.foodAllergy === true,
        "full-width": !(formik.values.dietaryPreferences === true && formik.values.foodAllergy === true),
    });

    const rowWidthMedicalClassName = classNames({
        "half-width": formik.values.medicalCondition === true && formik.values.longTermMedication === true,
        "full-width": !(formik.values.medicalCondition === true && formik.values.longTermMedication === true),
    });

    return (
        <>
            <Modal classes={{ root: "app-employee-first-time-info-modal" }} open={visible} aria-labelledby="employee-first-time-info-modal" aria-describedby="employee-first-time-info-modal">
                <div className="employee-first-time-info-modal">
                    <div className="employee-first-time-info-modal__main">
                        <div className="employee-first-time-info-modal__header">
                            <p className="employee-first-time-info-modal__title">Please fill in the required informations</p>
                            <p className="employee-first-time-info-modal__description">If you notice that the information provided by HR doesn't seem correct. <br />Kindly inform them to make the necessary corrections for accurate records.</p>
                        </div>
                        <form className="employee-first-time-info-modal__body" onSubmit={formik.handleSubmit}>


                            <AppInput disabled type="text" name="name" label="Full Name(as per NRIC)" value={employeeInfo?.personalInfo?.name} />


                            <AppInput required type="text" name="nickname" label="Nickname" placeholder="Nickname" disabled={formik.isSubmitting} value={formik.values.nickname} error={formik.errors.nickname} touched={formik.touched.nickname} onChange={formik.handleChange} />


                            <AppInput disabled type="text" name="identificationType" label="Identification Type" value={employeeInfo?.personalInfo?.identificationType} />


                            <AppInput disabled type="text" name="identificationNumber" label={employeeInfo?.personalInfo?.identificationType === "NRIC" ? "NRIC No" : "Passport No"} value={employeeInfo?.personalInfo?.identificationType === "NRIC" ? employeeInfo?.personalInfo?.nric || "" : employeeInfo?.personalInfo?.passport || ""} />


                            <AppCalendarInput disabled label="Date of Birth" displayFormat={calendarDisplayFormat} value={employeeInfo?.personalInfo?.dateOfBirth || ""} />


                            <AppButtonInput required name="gender" label="Gender" value={formik.values.gender || employeeInfo?.personalInfo?.gender} disabled={formik.isSubmitting} options={COMMON.DROPDOWNS.GENDER} error={formik.errors.gender} touched={formik.touched.gender} onChange={formik.setFieldValue} />


                            <AppSelectInput required type="text" name="nationality" label="Nationality" placeholder="Please Select" disabled={formik.isSubmitting} options={COMMON.DROPDOWNS.NATIONALITY} value={formik.values.nationality || employeeInfo?.personalInfo?.nationality} error={formik.errors.nationality} touched={formik.touched.nationality} onChange={onHandleSelectField} />


                            <AppSelectInput required type="text" name="maritalStatus" label="Marital Status" placeholder="Please Select" disabled={formik.isSubmitting} options={COMMON.DROPDOWNS.MARITAL_STATUS} value={formik.values.maritalStatus || employeeInfo?.personalInfo?.maritalStatus} error={formik.errors.maritalStatus} touched={formik.touched.maritalStatus} onChange={onHandleSelectField} />


                            <AppMobileInput disabled type="number" name="mobileNo" label="Mobile No." value={employeeInfo?.personalInfo?.contact?.mobileNo || ""} prefixNo={employeeInfo?.personalInfo?.contact?.mobileNoPrefix || ""} />


                            <AppInput disabled type="text" name="email" label="Personal Email" value={employeeInfo?.personalInfo?.contact?.email || ""} />


                            {/* Correspondence Address */}
                            <div className="employee-first-time-info-modal__section-title">Correspondence Address</div>


                            <AppInput required type="text" name="correspondenceAddress1" label="Residential Address" placeholder="Residential Address" value={formik.values.correspondenceAddress1} error={formik.errors.correspondenceAddress1} touched={formik.touched.correspondenceAddress1} onChange={formik.handleChange} disabled={formik.isSubmitting} />


                            <AppInput type="text" name="correspondenceAddress2" label="Address (Line 2)" placeholder="Address (Line 2)" value={formik.values.correspondenceAddress2} error={formik.errors.correspondenceAddress2} touched={formik.touched.correspondenceAddress2} onChange={formik.handleChange} disabled={formik.isSubmitting} />


                            <AppSelectInput required type="text" name="state" label="State" placeholder="Please Select" loadOptions={getStateListing} value={formik.values.state} error={formik.errors.state} touched={formik.touched.state} onChange={onHandleSelectField} disabled={formik.isSubmitting} />


                            <AppSelectInput required type="text" name="region" label="Region" placeholder="Please Select" disabled={formik.isSubmitting || !formik.values.state} loadOptions={getCitiesLoadOptions} value={formik.values.region} error={formik.errors.region} touched={formik.touched.region} onChange={onHandleSelectField} />


                            <AppSelectInput required type="text" name="postcode" label="Postcode" placeholder="Please Select" disabled={formik.isSubmitting || !formik.values.region} loadOptions={getPostcodeLoadOptions} value={formik.values.postcode} error={formik.errors.postcode} touched={formik.touched.postcode} onChange={onHandleSelectField} />


                            {/* Residential Address */}
                            <div className="employee-first-time-info-modal__section-title">Residential Address</div>


                            <div className="employee-first-time-info-modal__checkbox">
                                <AppCheckbox onClick={onHandleSelectPermanentAddress} name="permanentAddress" label="Same as Residential Address" disabled={formik.isSubmitting} value={formik.values.permanentAddress} />
                            </div>


                            {!formik.values.permanentAddress && (
                                <>

                                    <AppInput required type="text" name="residentialAddress1" label="Residential Address" placeholder="Residential Address" disabled={formik.isSubmitting} value={formik.values.residentialAddress1} error={formik.errors.residentialAddress1} touched={formik.touched.residentialAddress1} onChange={formik.handleChange} />


                                    <AppInput type="text" name="residentialAddress2" label="Residential Address (Line 2)" placeholder="Address (Line 2)" disabled={formik.isSubmitting} value={formik.values.residentialAddress2} error={formik.errors.residentialAddress2} touched={formik.touched.residentialAddress2} onChange={formik.handleChange} />


                                    <AppSelectInput required type="text" name="residentialState" label="State" placeholder="Please Select" disabled={formik.isSubmitting} loadOptions={getStateListing} value={formik.values.residentialState} error={formik.errors.residentialState} touched={formik.touched.residentialState} onChange={onHandleSelectField} />


                                    <AppSelectInput required type="text" name="residentialRegion" label="Region" placeholder="Please Select" disabled={formik.isSubmitting || !formik.values.residentialState} loadOptions={getResidentialCitiesLoadOptions} value={formik.values.residentialRegion} error={formik.errors.residentialRegion} touched={formik.touched.residentialRegion} onChange={onHandleSelectField} />


                                    <AppSelectInput required type="text" name="residentialPostcode" label="Postcode" placeholder="Please Select" disabled={formik.isSubmitting || !formik.values.residentialRegion} loadOptions={getResidentialPostcodeLoadOptions} value={formik.values.residentialPostcode} error={formik.errors.residentialPostcode} touched={formik.touched.residentialPostcode} onChange={onHandleSelectField} />

                                </>)}


                            {/* Emergency Contact */}
                            <div className="employee-first-time-info-modal__section-title">Emergency Contact</div>


                            <AppInput required type="text" name="emergencyContactFullName" label="Full Name" placeholder="Full Name" disabled={formik.isSubmitting} value={formik.values.emergencyContactFullName} error={formik.errors.emergencyContactFullName} touched={formik.touched.emergencyContactFullName} onChange={formik.handleChange} />


                            <AppSelectInput required type="text" name="emergencyContactRelationship" label="Relationship" placeholder="Please Select" disabled={formik.isSubmitting} loadOptions={getRelationshipListing} value={formik.values.emergencyContactRelationship} error={formik.errors.emergencyContactRelationship} touched={formik.touched.emergencyContactRelationship} onChange={formik.handleChange} />


                            <AppMobileInput required type="number" name="emergencyContactMobileNo" prefixNoName="emergencyContactMobileNoPrefix" label="Mobile No." disabled={formik.isSubmitting} placeholder="Mobile No." value={formik.values.emergencyContactMobileNo} prefixNo={formik.values.emergencyContactMobileNoPrefix} error={formik.errors.emergencyContactMobileNo} touched={formik.touched.emergencyContactMobileNo} onChange={formik.handleChange} onChangeCode={formik.setFieldValue} />

                            {/* Emergency Contact (Optional)*/}
                            <div className="employee-first-time-info-modal__section-title">Emergency Contact (Optional)</div>


                            <AppInput type="text" name="emergencyContactFullName2" label="Full Name" placeholder="Full Name" disabled={formik.isSubmitting} value={formik.values.emergencyContactFullName2} error={formik.errors.emergencyContactFullName} touched={formik.touched.emergencyContactFullName2} onChange={formik.handleChange} />


                            <AppSelectInput type="text" name="emergencyContactRelationship2" label="Relationship" placeholder="Please Select" disabled={formik.isSubmitting} loadOptions={getRelationshipListing} value={formik.values.emergencyContactRelationship2} error={formik.errors.emergencyContactRelationship2} touched={formik.touched.emergencyContactRelationship2} onChange={formik.handleChange} />


                            <AppMobileInput type="number" name="emergencyContactMobileNo2" prefixNoName="emergencyContactMobileNoPrefix2" label="Mobile No." disabled={formik.isSubmitting} placeholder="Mobile No." value={formik.values.emergencyContactMobileNo2} prefixNo={formik.values.emergencyContactMobileNoPrefix2} error={formik.errors.emergencyContactMobileNo2} touched={formik.touched.emergencyContactMobileNo2} onChange={formik.handleChange} onChangeCode={formik.setFieldValue} />


                            {/* Health Declaration & Consent */}
                            <div className="employee-first-time-info-modal__section-title">Health Declaration & Consent</div>

                            <AppButtonInput
                                required
                                name="dietaryPreferences"
                                label="Any dietary preferences or restrictions?"
                                disabled={formik.isSubmitting}
                                options={[
                                    { label: "Yes", value: true },
                                    { label: "No", value: false },
                                ]}
                                value={formik.values.dietaryPreferences}
                                error={formik.errors.dietaryPreferences}
                                touched={formik.touched.dietaryPreferences}
                                onChange={formik.setFieldValue}
                            />

                            <AppButtonInput
                                required
                                name="foodAllergy"
                                label="Any food allergies or intolerances?"
                                disabled={formik.isSubmitting}
                                options={[
                                    { label: "Yes", value: true },
                                    { label: "No", value: false },
                                ]}
                                value={formik.values.foodAllergy}
                                error={formik.errors.foodAllergy}
                                touched={formik.touched.foodAllergy}
                                onChange={formik.setFieldValue}
                            />

                            {formik.values.dietaryPreferences === true && (
                                <div className={rowWidthDietaryClassName}>
                                    <div className="employee-first-time-info-modal__label">
                                        Please specify your dietary preferences:
                                    </div>
                                    {dietarySelections.map((option) => (
                                        <div className="employee-first-time-info-modal__checkbox">
                                            <AppCheckbox
                                                key={option.value}
                                                label={option.label}
                                                disabled={formik.isSubmitting}
                                                value={formik.values.dietaryOptions.includes(option.value)}
                                                onClick={() => {
                                                    const current = formik.values.dietaryOptions;
                                                    const updated = formik.values.dietaryOptions.includes(option.value)
                                                        ? current.filter((item) => item !== option.value)
                                                        : [...current, option.value];
                                                    formik.setFieldTouched("dietaryOptions", true);
                                                    formik.setFieldValue("dietaryOptions", updated);
                                                }}
                                            />
                                        </div>
                                    ))}

                                    {formik.touched.dietaryOptions && formik.errors.dietaryOptions && (
                                        <div className="error-message">
                                            {formik.errors.dietaryOptions}
                                        </div>
                                    )}

                                    {formik.values.dietaryOptions.includes("OTHER") && (
                                        <AppInput
                                            name="dietaryDescription"
                                            label="Other Preferences"
                                            disabled={formik.isSubmitting}
                                            placeholder="Please specify"
                                            value={formik.values.dietaryDescription}
                                            error={formik.errors.dietaryDescription}
                                            touched={formik.touched.dietaryDescription}
                                            onChange={formik.handleChange}
                                        />
                                    )}
                                </div>
                            )}

                            {formik.values.foodAllergy === true && formik.values.dietaryPreferences !== true && (
                                <div className="half-width invi-row"></div>
                            )}

                            {formik.values.foodAllergy === true && (
                                <div className="half-width">
                                    <AppInput
                                        name="foodAllergyDetails"
                                        label="Please specify"
                                        disabled={formik.isSubmitting}
                                        placeholder="Please specify"
                                        value={formik.values.foodAllergyDetails}
                                        error={formik.errors.foodAllergyDetails}
                                        touched={formik.touched.foodAllergyDetails}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            )}

                            <AppButtonInput
                                required
                                name="medicalCondition"
                                label="Any medical condition?"
                                disabled={formik.isSubmitting}
                                options={[
                                    { label: "Yes", value: true },
                                    { label: "No", value: false },
                                ]}
                                value={formik.values.medicalCondition}
                                error={formik.errors.medicalCondition}
                                touched={formik.touched.medicalCondition}
                                onChange={formik.setFieldValue}
                            />

                            <AppButtonInput
                                required
                                name="longTermMedication"
                                label="Taking any long-term medication?"
                                disabled={formik.isSubmitting}
                                options={[
                                    { label: "Yes", value: true },
                                    { label: "No", value: false },
                                ]}
                                value={formik.values.longTermMedication}
                                error={formik.errors.longTermMedication}
                                touched={formik.touched.longTermMedication}
                                onChange={formik.setFieldValue}
                            />


                            {formik.values.medicalCondition === true && (
                                <div className={rowWidthMedicalClassName}>
                                    <AppInput
                                        name="medicalConditionDetails"
                                        label="Please specify"
                                        disabled={formik.isSubmitting}
                                        placeholder="Please specify"
                                        value={formik.values.medicalConditionDetails}
                                        error={formik.errors.medicalConditionDetails}
                                        touched={formik.touched.medicalConditionDetails}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            )}

                            {formik.values.medicalCondition !== true && formik.values.longTermMedication === true && (
                                <div className="half-width invi-row"></div>
                            )}

                            {formik.values.longTermMedication === true && (
                                <div className="half-width">
                                    <AppInput
                                        name="longTermMedicationDetails"
                                        label="Please specify"
                                        disabled={formik.isSubmitting}
                                        placeholder="Please specify"
                                        value={formik.values.longTermMedicationDetails}
                                        error={formik.errors.longTermMedicationDetails}
                                        touched={formik.touched.longTermMedicationDetails}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                            )}

                            <div className="full-width">
                                <AppCheckbox
                                    required
                                    onClick={onHandleCheckbox}
                                    name="infoConfirmed"
                                    label={
                                        <>
                                            I confirm that the information provided is accurate to the best of my knowledge.
                                            <span className="required">*</span>									
                                        </>
                                    }
                                    disabled={formik.isSubmitting}
                                    value={formik.values.infoConfirmed}
                                    error={formik.errors.infoConfirmed}
                                    touched={formik.touched.infoConfirmed}
                                />
                                <div className="error-message">{formik.touched.infoConfirmed && formik.errors.infoConfirmed}</div>
                            </div>
                            
                            <div className="full-width">
                                <AppCheckbox 
                                    required 
                                    onClick={onHandleCheckbox} 
                                    name="acknowledgePolicy" 
                                    label={
                                        <>
                                            I understand that providing false or misleading information may result in workplace actions.
                                            <span className="required">*</span>	
                                        </>
                                    }
                                    disabled={formik.isSubmitting}
                                    value={formik.values.acknowledgePolicy}
                                    error={formik.errors.acknowledgePolicy}
                                    touched={formik.touched.acknowledgePolicy}
                                />
                                <div className="error-message">{formik.touched.acknowledgePolicy && formik.errors.acknowledgePolicy}</div>                                
                            </div>

                            {/* Bank Details */}
                            <div className="employee-first-time-info-modal__section-title">Bank Details</div>


                            <AppSelectInput ref={bankRef} required type="text" name="bank" label="Bank Name" placeholder="Please Select" disabled={formik.isSubmitting} loadOptions={getBankListing} value={formik.values.bank} error={formik.errors.bank} touched={formik.touched.bank} onChange={formik.handleChange} />


                            <AppInput required type="number" name="bankAccountNo" label="Bank Account No." placeholder="Bank Account No." disabled={formik.isSubmitting} value={formik.values.bankAccountNo} error={formik.errors.bankAccountNo} touched={formik.touched.bankAccountNo} onChange={formik.handleChange} />


                            <AppInput type="text" name="swiftCode" label="Swift Code / Branch Name" placeholder="Swift Code / Branch Name" disabled={formik.isSubmitting} value={formik.values.swiftCode} touched={formik.touched.swiftCode} onChange={formik.handleChange} />


                            {/* Contributions */}
                            <div className="employee-first-time-info-modal__section-title">Contributions</div>


                            <AppInput type="text" name="epfNo" label="EPF No." placeholder="EPF No." disabled={formik.isSubmitting} value={formik.values.epfNo} touched={formik.touched.epfNo} onChange={formik.handleChange} />


                            <AppInput type="text" name="incomeTaxNo" label="Income Tax No." placeholder="Income Tax No." disabled={formik.isSubmitting} value={formik.values.incomeTaxNo} touched={formik.touched.incomeTaxNo} onChange={formik.handleChange} />


                            <AppInput type="text" name="socsoNo" label="Socso No." placeholder="Socso No." disabled={formik.isSubmitting} value={formik.values.socsoNo} touched={formik.touched.socsoNo} onChange={formik.handleChange} />


                            <div className="employee-first-time-info-modal__button-container">
                                <AppButton type="submit" label="Submit" disabled={formik.isSubmitting} />
                            </div>
                        </form>

                    </div>
                </div >
            </Modal >

            <AppWelcomeOnboardModal ref={welcomeRef} />
        </>
    )

});

export default memo(compose(withProfile)(AppEmployeeFirstTimeInfoModal));